import { MODAL_VIDEOS_VIMEO_ALLOWED } from '../../../constants/modal-videos';

/**
 * Campanha #mostraseuaprova — pop-ups de convite para gravar um vídeo.
 *
 * São 10 variações, uma por produto da plataforma, disparadas logo depois que o
 * aluno conclui a ação correspondente (ver `trigger`). O visual segue três temas
 * que se alternam pela grade do Figma: `paper`, `yellow` e `dark`.
 */

export type MostraSeuAprovaTheme = 'paper' | 'yellow' | 'dark';

/** As 10 variações por produto, disparadas por uma ação concluída na plataforma. */
/** As 10 variações com conteúdo próprio, uma por produto. */
export type MostraSeuAprovaContentKey =
  | 'cursos-regulares'
  | 'cronograma-personalizado'
  | 'simulador'
  | 'redacao'
  | 'regionais'
  | 'simulados-ineditos-enem'
  | 'esquadrao-de-elite'
  | 'banco-de-questoes'
  | 'cursos-complementares'
  | 'flashcards';

/**
 * De onde o gatilho partiu. Nem todo ponto de disparo tem conteúdo próprio: a
 * navegação livre das disciplinas reaproveita o texto de `cursos-regulares`,
 * que serve para qualquer videoaula, mas é registrada à parte para o relatório
 * separar as duas origens.
 */
export type MostraSeuAprovaTriggerKey =
  | MostraSeuAprovaContentKey
  | 'nav-livre';

/** Chave da apresentação da campanha. Não tem gatilho e não ocupa slot. */
export type MostraSeuAprovaPresentationKey = 'apresentacao';

export type MostraSeuAprovaKey =
  | MostraSeuAprovaContentKey
  | MostraSeuAprovaPresentationKey;

export interface iMostraSeuAprovaContent {
  key: MostraSeuAprovaKey;
  theme: MostraSeuAprovaTheme;
  /** Momento em que o pop-up deve aparecer. Ausente na apresentação. */
  trigger?: string;
  /**
   * Token do vídeo no Vimeo. Quando presente, o vídeo ocupa o corpo do modal e
   * `body` vira uma linha de apoio — usado pela apresentação da campanha.
   */
  vimeoToken?: string;
  /** Ausentes no modal de vídeo, que não exibe texto nenhum. */
  eyebrow?: string;
  body?: string;
  /** Sempre presente: nomeia o diálogo para leitores de tela. */
  title: string;
}

export interface iMostraSeuAprovaThemeTokens {
  background: string;
  text: string;
  /** Cor da sombra dura de 8px do card. */
  shadow: string;
  sparkle: string;
  /** Textura de fundo do card (grade quadriculada ou bolinhas). */
  pattern: {
    backgroundImage: string;
    backgroundSize: string;
  };
}

const GRID_LINE = 'rgba(0, 0, 0, 0.13)';
const DOT = 'rgba(255, 242, 0, 0.22)';

const GRID_PATTERN = {
  backgroundImage: [
    `repeating-linear-gradient(to right, ${GRID_LINE} 0 1px, transparent 1px 40px)`,
    `repeating-linear-gradient(to bottom, ${GRID_LINE} 0 1px, transparent 1px 40px)`,
  ].join(', '),
  backgroundSize: '40px 40px, 40px 40px',
};

const DOT_PATTERN = {
  backgroundImage: `radial-gradient(${DOT} 1.5px, transparent 1.5px)`,
  backgroundSize: '25px 25px',
};

export const MOSTRA_SEU_APROVA_THEMES: Record<
  MostraSeuAprovaTheme,
  iMostraSeuAprovaThemeTokens
> = {
  paper: {
    background: '#ECEEE4',
    text: '#000000',
    shadow: '#FF5227',
    sparkle: '#FF5227',
    pattern: GRID_PATTERN,
  },
  yellow: {
    background: '#FFF200',
    text: '#000000',
    shadow: '#000000',
    sparkle: '#FF5227',
    pattern: GRID_PATTERN,
  },
  dark: {
    background: '#000000',
    text: '#FCFCFC',
    shadow: '#FFF200',
    sparkle: '#FFF200',
    pattern: DOT_PATTERN,
  },
};

export const MOSTRA_SEU_APROVA_CTA_LABEL = 'Quero garantir minha vaga';
export const MOSTRA_SEU_APROVA_DISMISS_LABEL = 'Agora não';
export const MOSTRA_SEU_APROVA_HASHTAG = '#mostraseuaprova';
export const MOSTRA_SEU_APROVA_AUTHOR = 'Jubilut';

export const MOSTRA_SEU_APROVA_PRESENTATION_KEY: MostraSeuAprovaPresentationKey =
  'apresentacao';

const MOSTRA_SEU_APROVA_VIDEO = MODAL_VIDEOS_VIMEO_ALLOWED.MOSTRA_SEU_APROVA;

export const MOSTRA_SEU_APROVA_CONTENTS: Record<
  MostraSeuAprovaKey,
  iMostraSeuAprovaContent
> = {
  // Abre a campanha para quem ainda não se inscreveu, no primeiro acesso dentro
  // da janela. Não depende de gatilho e não consome nenhum dos três slots.
  //
  // Exibe só o vídeo: quem apresenta a campanha é o Jubilut, e o `title` existe
  // apenas para nomear o diálogo a leitores de tela. Por isso, `allowed: false`
  // no registro de vídeos deixa este conteúdo sem nada para mostrar — desligar
  // o vídeo significa desligar a apresentação.
  apresentacao: {
    key: 'apresentacao',
    theme: 'paper',
    vimeoToken: MOSTRA_SEU_APROVA_VIDEO.allowed
      ? MOSTRA_SEU_APROVA_VIDEO.token
      : undefined,
    title: 'Chegou o #mostraseuaprova',
  },
  'cursos-regulares': {
    key: 'cursos-regulares',
    theme: 'paper',
    trigger: 'terminou uma videoaula',
    eyebrow: 'Ei, eu sei que você tá estudando, mas não fecha ainda...',
    title: 'Essa aula te ajudou?',
    body: 'Então grava um minuto mostrando ela. Só isso já garante sua vaga na aula de estratégia com o Fredão. E se o seu vídeo for o que for mais longe, uma hora comigo, só nós dois.',
  },
  'cronograma-personalizado': {
    key: 'cronograma-personalizado',
    theme: 'yellow',
    trigger: 'montou ou refez o cronograma',
    eyebrow: 'Rapidinho, antes de você voltar pro estudo.',
    title: 'Sua semana tá montada, que tal mostrar como ficou?',
    body: 'Um vídeo de um minuto do celular mostrando seu cronograma e você garante o aulão do Fredão sobre TRI. Top 26 entra nas mentorias até a prova, e o 1º lugar leva uma hora sozinho comigo.',
  },
  simulador: {
    key: 'simulador',
    theme: 'dark',
    trigger: 'terminou um simulado',
    eyebrow: 'Terminou? Então lê esse recadinho.',
    title: 'Como esse simulado te ajudou a estudar?',
    body: 'Mostra isso num vídeo de um minuto e garanta sua vaga na aula de estratégia com o Fredão. Você também concorre a uma hora a sós comigo pra montar seu plano até a prova.',
  },
  redacao: {
    key: 'redacao',
    theme: 'paper',
    trigger: 'recebeu a correção',
    eyebrow: 'Sua redação voltou? Calma, não fecha.',
    title: 'Correção humana na redação você só encontra aqui.',
    body: 'Grava um vídeo mostrando a correção e sua vaga no aulão do Fredão tá garantida. Top 26 entra nas mentorias até a prova, e o 1º lugar leva uma hora comigo.',
  },
  regionais: {
    key: 'regionais',
    theme: 'yellow',
    trigger: 'assistiu uma aula regional',
    eyebrow: 'Ei, sei que você tá focado. Um minuto só.',
    title: 'As regionais estão te ajudando?',
    body: 'Mostre como você estuda a História e a Geografia do seu vestibular. Vale sua vaga na aula de estratégia com o Fredão, e pode valer uma hora sozinho comigo.',
  },
  'simulados-ineditos-enem': {
    key: 'simulados-ineditos-enem',
    theme: 'dark',
    trigger: 'concluiu uma aplicação',
    eyebrow: 'Fechou o simulado? Então me dá um minuto.',
    title: 'Questão inédita, prova de verdade.',
    body: 'Mostra como é treinar com questões que ninguém respondeu antes. Sua vaga na aula de estratégia com o Fredão fica garantida, e o melhor vídeo ganha uma hora comigo, só nós dois.',
  },
  'esquadrao-de-elite': {
    key: 'esquadrao-de-elite',
    theme: 'paper',
    trigger: 'avançou no curso',
    eyebrow: 'Eu sei que você tá no meio do método. Mas presta atenção.',
    title: 'Mostra como você usa o Esquadrão de Elite.',
    body: 'Um minuto de vídeo e sua vaga na aula de estratégia com o Fredão tá garantida. E um vídeo vai garantir uma hora sozinho comigo, o autor do método.',
  },
  'banco-de-questoes': {
    key: 'banco-de-questoes',
    theme: 'yellow',
    trigger: 'fechou uma sequência',
    eyebrow: 'Acabou a lista? Não fecha ainda.',
    title: 'Grava como você treina aqui.',
    body: 'Um minuto mostrando o Banco de Questões vale sua vaga na aula de estratégia com o Fredão, e pode valer uma hora a sós comigo.',
  },
  'cursos-complementares': {
    key: 'cursos-complementares',
    theme: 'dark',
    trigger: 'concluiu um minicurso',
    eyebrow: 'Rapidinho, que eu sei que você tá estudando.',
    title: 'Muita gente nem sabe que esse minicurso existe.',
    body: 'Mostra ele num vídeo de um minuto. Garante sua vaga na aula de estratégia com o Fredão e concorre a uma hora sozinho comigo.',
  },
  flashcards: {
    key: 'flashcards',
    theme: 'paper',
    trigger: 'fechou uma revisão',
    eyebrow: 'Revisão fechada? Então me escuta um segundo.',
    title: 'Mostra como você fixa o conteúdo.',
    body: 'Um minuto de vídeo com seus flashcards garante sua vaga na aula de estratégia com o Fredão. E o vídeo que for mais longe leva uma hora comigo.',
  },
};

export const MOSTRA_SEU_APROVA_KEYS = Object.keys(
  MOSTRA_SEU_APROVA_CONTENTS
) as MostraSeuAprovaKey[];

/** Conteúdos que um gatilho pode exibir — a apresentação fica de fora. */
export const MOSTRA_SEU_APROVA_CONTENT_KEYS = MOSTRA_SEU_APROVA_KEYS.filter(
  (key): key is MostraSeuAprovaContentKey =>
    key !== MOSTRA_SEU_APROVA_PRESENTATION_KEY
);

/**
 * Qual conteúdo cada origem exibe. Só precisa de entrada quem não tem conteúdo
 * com o próprio nome; o resto cai na chave em si.
 */
const CONTENT_BY_TRIGGER: Partial<
  Record<MostraSeuAprovaTriggerKey, MostraSeuAprovaContentKey>
> = {
  'nav-livre': 'cursos-regulares',
};

export function getContentKeyForTrigger(
  triggerKey: MostraSeuAprovaTriggerKey
): MostraSeuAprovaContentKey {
  return CONTENT_BY_TRIGGER[triggerKey] ?? (triggerKey as MostraSeuAprovaContentKey);
}

/** Todas as origens de gatilho, para validar o que vem guardado. */
export const MOSTRA_SEU_APROVA_TRIGGER_KEYS: MostraSeuAprovaTriggerKey[] = [
  ...MOSTRA_SEU_APROVA_CONTENT_KEYS,
  'nav-livre',
];
